import { useEffect, useState } from 'react';

// Lightweight localStorage shortlist for the price-analysis tool: an ephemeral
// "creators I want to price-check", max 10. (The booking cart and AI-brief request
// stores it used to sit alongside were retired with the job-board pivot.)

export interface ShortlistItem {
  providerId: string;
  handle: string;
  followers?: number | null;
}

const KEY = 'kgdb_price_shortlist_v1';
export const SHORTLIST_MAX = 10;

function read(): ShortlistItem[] {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) || '[]');
    return Array.isArray(v) ? v : [];
  } catch {
    return [];
  }
}
function write(items: ShortlistItem[]) {
  try { localStorage.setItem(KEY, JSON.stringify(items)); } catch { /* quota */ }
  window.dispatchEvent(new Event('kgdb-shortlist-change'));
}

export function useShortlist() {
  const [items, setItems] = useState<ShortlistItem[]>(read);
  useEffect(() => {
    const on = () => setItems(read());
    window.addEventListener('kgdb-shortlist-change', on);
    window.addEventListener('storage', on);
    return () => {
      window.removeEventListener('kgdb-shortlist-change', on);
      window.removeEventListener('storage', on);
    };
  }, []);

  const has = (id: string) => items.some((i) => i.providerId === id);

  // returns { ok, full } so callers can surface "shortlist full" (max 10)
  const add = (item: ShortlistItem): { ok: boolean; full: boolean } => {
    const cur = read();
    if (cur.some((i) => i.providerId === item.providerId)) return { ok: true, full: false };
    if (cur.length >= SHORTLIST_MAX) return { ok: false, full: true };
    write([...cur, item]);
    return { ok: true, full: false };
  };
  const remove = (id: string) => write(read().filter((i) => i.providerId !== id));
  const toggle = (item: ShortlistItem): { ok: boolean; full: boolean } =>
    (has(item.providerId) ? (remove(item.providerId), { ok: true, full: false }) : add(item));
  const clear = () => write([]);

  return { items, count: items.length, max: SHORTLIST_MAX, has, add, remove, toggle, clear };
}
